'use client';

import { useState, useEffect, useCallback } from 'react';

export type ConsentPreferences = {
  necessary: boolean; // Always true, required for the site to function
  analytics: boolean; // Google Analytics, Vercel Analytics
  marketing: boolean; // Marketing/advertising cookies
  preferences: boolean; // User preference cookies
};

export type ConsentStatus = 'pending' | 'accepted' | 'rejected' | 'customized';

const CONSENT_COOKIE_NAME = 'cookie_consent';
const CONSENT_VERSION = '1.0'; // Increment when privacy policy changes

const DEFAULT_PREFERENCES: ConsentPreferences = {
  necessary: true,
  analytics: true,  // Track by default unless user explicitly rejects
  marketing: true,  // Track by default unless user explicitly rejects
  preferences: true,
};

function getStoredConsent(): { status: ConsentStatus; preferences: ConsentPreferences; version: string } | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(CONSENT_COOKIE_NAME);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    
    // Check if consent version matches
    if (parsed.version !== CONSENT_VERSION) {
      // Privacy policy changed, need to re-consent
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function setStoredConsent(status: ConsentStatus, preferences: ConsentPreferences): void {
  if (typeof window === 'undefined') return;

  const data = {
    status,
    preferences,
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
  };

  localStorage.setItem(CONSENT_COOKIE_NAME, JSON.stringify(data));

  // Also set a cookie for server-side access
  const expiryDate = new Date();
  expiryDate.setFullYear(expiryDate.getFullYear() + 1);
  document.cookie = `${CONSENT_COOKIE_NAME}=${status};expires=${expiryDate.toUTCString()};path=/;SameSite=Lax`;
}

export function useCookieConsent() {
  const [status, setStatus] = useState<ConsentStatus>('pending');
  const [preferences, setPreferences] = useState<ConsentPreferences>(DEFAULT_PREFERENCES);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load stored consent on mount
  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setStatus(stored.status);
      setPreferences(stored.preferences);
    }
    setIsLoaded(true);
  }, []);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const newPreferences: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setPreferences(newPreferences);
    setStatus('accepted');
    setStoredConsent('accepted', newPreferences);

    // Enable analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted',
      });
    }
  }, []);

  // Reject all non-essential cookies
  const rejectAll = useCallback(() => {
    const newPreferences: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    setPreferences(newPreferences);
    setStatus('rejected');
    setStoredConsent('rejected', newPreferences);

    // Disable analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
      });
    }
  }, []);

  // Save custom preferences
  const savePreferences = useCallback((customPreferences: Partial<ConsentPreferences>) => {
    const newPreferences: ConsentPreferences = {
      ...preferences,
      ...customPreferences,
      necessary: true, // Always required
    };
    setPreferences(newPreferences);
    setStatus('customized');
    setStoredConsent('customized', newPreferences);

    // Update analytics consent
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: newPreferences.analytics ? 'granted' : 'denied',
        ad_storage: newPreferences.marketing ? 'granted' : 'denied',
      });
    }
  }, [preferences]);

  // Reset consent (for testing or user request)
  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_COOKIE_NAME);
    document.cookie = `${CONSENT_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    setStatus('pending');
    setPreferences(DEFAULT_PREFERENCES);
  }, []);

  return {
    status,
    preferences,
    isLoaded,
    showBanner: isLoaded && status === 'pending',
    acceptAll,
    rejectAll,
    savePreferences,
    resetConsent,
    hasConsented: status !== 'pending',
  };
}

// Add gtag type to window
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

